
import React from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useCreateProject } from '@/hooks/useProjects';
import { useCreateProjectRrwebSession, ProjectRrwebSession } from '@/hooks/useProjectRrwebSessions';
import { RrwebSession } from '@/hooks/useRrwebSessions';
import { useNavigate } from 'react-router-dom';
import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SessionSelectionPanel from '../sessions/SessionSelectionPanel';

const projectSchema = z.object({
  title: z.string().min(1, "Project title is required").max(100, "Title too long"),
  description: z.string().optional(),
});

type ProjectFormData = z.infer<typeof projectSchema>;

interface CreateProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CreateProjectModal = ({ open, onOpenChange }: CreateProjectModalProps) => {
  const navigate = useNavigate();
  const createProject = useCreateProject();
  const createProjectRrwebSession = useCreateProjectRrwebSession();
  
  const [currentStep, setCurrentStep] = React.useState(1);
  const [selectedSessions, setSelectedSessions] = React.useState<RrwebSession[]>([]);
  const [isCreating, setIsCreating] = React.useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectSchema),
  });

  const handleClose = () => {
    onOpenChange(false);
    setCurrentStep(1);
    setSelectedSessions([]);
    reset();
  };

  const handleSessionSelect = (session: RrwebSession) => {
    setSelectedSessions(prev => [...prev, session]);
  };

  const handleSessionDeselect = (session: RrwebSession) => {
    setSelectedSessions(prev => prev.filter(s => s.id !== session.id));
  };

  const onSubmit = async (data: ProjectFormData) => {
    setIsCreating(true);
    try {
      // Create the project first
      const project = await createProject.mutateAsync({
        title: data.title,
        description: data.description || undefined,
      });

      // Link selected sessions to the project
      if (selectedSessions.length > 0) {
        const sessionPromises = selectedSessions.map((session, index) =>
          createProjectRrwebSession.mutateAsync({
            project_id: project.id,
            rrweb_session_id: session.id,
            order_in_project: index + 1,
          })
        );
        
        await Promise.all(sessionPromises);
      }

      handleClose();
      toast.success('Project created successfully!');
      navigate(`/studio/projects/${project.id}`);
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create project. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <form onSubmit={handleSubmit(() => setCurrentStep(2))} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-white">
                Project Title
              </Label>
              <Input
                id="title"
                {...register('title')}
                placeholder="Enter project title..."
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
              />
              {errors.title && (
                <p className="text-red-400 text-sm">{errors.title.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-white">
                Description (Optional)
              </Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Describe your project..."
                rows={3}
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-400 resize-none"
              />
              {errors.description && (
                <p className="text-red-400 text-sm">{errors.description.message}</p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              >
                Next <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </form>
        );

      case 2:
        return (
          <div className="space-y-4">
            <SessionSelectionPanel
              selectedSessions={selectedSessions}
              onSessionSelect={handleSessionSelect}
              onSessionDeselect={handleSessionDeselect}
            />

            <div className="flex gap-3 pt-4 border-t border-white/20">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCurrentStep(1)}
                className="border-white/20 text-white hover:bg-white/10"
                disabled={isCreating}
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                type="button"
                onClick={handleSubmit(onSubmit)}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                disabled={isCreating}
              >
                {isCreating ? 'Creating Project...' : 'Create Project'}
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className={`bg-slate-900 border-white/20 text-white ${
          currentStep === 2 ? 'max-w-4xl' : 'max-w-md'
        }`}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {currentStep === 1 ? 'Create New Project' : 'Select Recording Sessions'}
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {currentStep === 1 
              ? 'Start a new video project with AI-powered editing tools.'
              : 'Choose which screen recordings to include in your project.'
            }
          </DialogDescription>
        </DialogHeader>

        {renderStepContent()}
      </DialogContent>
    </Dialog>
  );
};

export default CreateProjectModal;
